import React, { useState, useEffect } from 'react';
import {
  FileText, Calendar, User, Building2, Package, Eye, Download,
  X, ChevronLeft, ChevronRight, Search, Plus, Trash2, Edit2,
  CheckCircle, XCircle, Clock, Save, Printer, AlertCircle, Upload, RotateCcw, Users
} from 'lucide-react';
import API_BASE_URL from '../config';
import Swal from 'sweetalert2';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import megalogo from '../Image/megalogo.png';

export default function Reports() {
  const [reports, setReports] = useState([]);
  const [filteredReports, setFilteredReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [departmentFilter, setDepartmentFilter] = useState('all');
  const [showFormModal, setShowFormModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedReport, setSelectedReport] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [websiteInfo, setWebsiteInfo] = useState(null);
  const [customLogo, setCustomLogo] = useState(null);
  const [showLogoUploader, setShowLogoUploader] = useState(false);

  // ✅ NEW: Grouped Reports and Selected Name
  const [groupedReports, setGroupedReports] = useState([]);
  const [selectedNameForPDF, setSelectedNameForPDF] = useState(null);

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);

  // Form Data
  const [formData, setFormData] = useState({
    date: new Date().toISOString().split('T')[0],
    name: '',
    arInvoiceNo: '',
    controlNo: '',
    department: '',
    branchSpecify: '',
    items: [
      { itemCode: '', qty: '', uom: '', itemDescription: '', purpose: '' }
    ],
    requestedBy: '',
    approvedBy: '',
    releasedBy: '',
    remarks: ''
  });

  const departments = [
    'Mechanical', 'QC', 'Accounting', 'Logistics', 'Spareparts',
    'Gen. Services', 'Production', 'RM Whse', 'Packaging',
    'Electrical', 'FG Whse', 'Motorpool', 'Admin/HR/MIS', 'Fabrication'
  ];

  useEffect(() => {
    fetchReports();
    fetchWebsiteInfo();
  }, []);

  useEffect(() => {
    filterReports();
  }, [searchTerm, statusFilter, departmentFilter, reports]);

  const fetchReports = async () => {
    setLoading(true);
    try {
      const headerRes = await fetch(`${API_BASE_URL}api/MRHeader`);
      if (!headerRes.ok) {
        setReports([]);
        setFilteredReports([]);
        return;
      }

      const headerData = await headerRes.json();
      const headerArray = Array.isArray(headerData) ? headerData : (headerData ? [headerData] : []);

      const reportsWithDetails = await Promise.all(
        headerArray.map(async (header) => {
          try {
            const detailsRes = await fetch(`${API_BASE_URL}api/MRDetails/by-header/${header.id}`);
            if (detailsRes.ok) {
              const details = await detailsRes.json();
              return { ...header, mrDetails: Array.isArray(details) ? details : [] };
            }
            return { ...header, mrDetails: [] };
          } catch (err) {
            return { ...header, mrDetails: [] };
          }
        })
      );

      setReports(reportsWithDetails);
      setFilteredReports(reportsWithDetails);
      groupReportsByDeptAndControl(reportsWithDetails);
    } catch (error) {
      console.error('Error fetching reports:', error);
      setReports([]);
      setFilteredReports([]);
    } finally {
      setLoading(false);
    }
  };

  // ✅ GROUP REPORTS BY DEPARTMENT + CONTROL NO
  const groupReportsByDeptAndControl = (reportsData) => {
    const grouped = {};

    reportsData.forEach(report => {
      const dept = report.department || report.departmentName || 'N/A';
      const controlNo = report.controlNo || report.mrNo || 'N/A';
      const key = `${dept}|||${controlNo}`;

      if (!grouped[key]) {
        grouped[key] = {
          department: dept,
          controlNo: controlNo,
          reports: [],
          names: [],
          allItems: [],
          status: report.status,
          mrDate: report.mrDate,
          arInvoiceNo: report.arInvoiceNo
        };
      }

      grouped[key].reports.push(report);
      grouped[key].names.push({
        id: report.id,
        name: report.requestedByName,
        fullReport: report
      });
      grouped[key].allItems.push(...(report.mrDetails || []));
    });

    const groupedArray = Object.values(grouped);
    setGroupedReports(groupedArray);
  };

  const fetchWebsiteInfo = async () => {
    try {
      const res = await fetch(`${API_BASE_URL}api/Website_Details`);
      if (!res.ok) throw new Error('Failed to fetch website info');
      const data = await res.json();
      const activeWebsite = data.find(w => w.isActive) || data[0];
      if (activeWebsite) {
        setWebsiteInfo(activeWebsite);
      }
    } catch (error) {
      console.error('Error fetching website info:', error);
    }
  };

  const filterReports = () => {
    let filtered = [...reports];

    if (searchTerm) {
      filtered = filtered.filter(report =>
        report.controlNo?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        report.mrNo?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        report.arInvoiceNo?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        report.requestedByName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        report.department?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        report.departmentName?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    if (statusFilter !== 'all') {
      filtered = filtered.filter(report => report.status === parseInt(statusFilter));
    }

    if (departmentFilter !== 'all') {
      filtered = filtered.filter(report =>
        report.departmentName === departmentFilter ||
        report.department === departmentFilter
      );
    }

    setFilteredReports(filtered);
    setCurrentPage(1);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleItemChange = (index, field, value) => {
    const newItems = [...formData.items];
    newItems[index][field] = value;
    setFormData({ ...formData, items: newItems });
  };

  const addItemRow = () => {
    setFormData({
      ...formData,
      items: [...formData.items, { itemCode: '', qty: '', uom: '', itemDescription: '', purpose: '' }]
    });
  };

  const removeItemRow = (index) => {
    if (formData.items.length > 1) {
      const newItems = formData.items.filter((_, i) => i !== index);
      setFormData({ ...formData, items: newItems });
    }
  };

  const resetForm = () => {
    setFormData({
      date: new Date().toISOString().split('T')[0],
      name: '',
      arInvoiceNo: '',
      controlNo: '',
      department: '',
      branchSpecify: '',
      items: [{ itemCode: '', qty: '', uom: '', itemDescription: '', purpose: '' }],
      requestedBy: '',
      approvedBy: '',
      releasedBy: '',
      remarks: ''
    });
    setEditMode(false);
  };

  const handleSubmit = async () => {
    if (!formData.name || !formData.controlNo || !formData.department) {
      Swal.fire({
        icon: 'error',
        title: 'Validation Error',
        text: 'Please fill in all required fields (Name, Control No, Department)',
        confirmButtonColor: '#dc2626'
      });
      return;
    }

    const payload = {
      mrDate: formData.date,
      requestedByName: formData.name,
      arInvoiceNo: formData.arInvoiceNo,
      controlNo: formData.controlNo,
      department: formData.department,
      branchSpecify: formData.branchSpecify,
      status: 0,
      remarks: formData.remarks,
      mrDetails: formData.items.filter(item => item.itemCode).map(item => ({
        itemCode: item.itemCode,
        requestedQty: parseFloat(item.qty) || 0,
        uom: item.uom,
        itemDescription: item.itemDescription,
        purpose: item.purpose
      }))
    };

    try {
      const res = await fetch(`${API_BASE_URL}api/MR_Header`, {
        method: editMode ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      if (res.ok) {
        await Swal.fire({
          icon: 'success',
          title: 'Success!',
          text: `Report ${editMode ? 'updated' : 'created'} successfully`,
          confirmButtonColor: '#10b981',
          timer: 2000
        });
        setShowFormModal(false);
        resetForm();
        fetchReports();
      } else {
        throw new Error('Failed to save report');
      }
    } catch (error) {
      console.error('Error saving report:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Failed to save report. Please try again.',
        confirmButtonColor: '#dc2626'
      });
    }
  };

  // ✅ VIEW GROUPED REPORT WITH NAME SELECTION
  const handleViewGroupedReport = async (group) => {
    setSelectedNameForPDF(group.names[0]);
    
    const mergedReport = {
      ...group.names[0].fullReport,
      mrDetails: group.allItems,
      groupedNames: group.names,
      isGrouped: true
    };

    setSelectedReport(mergedReport);
    setShowViewModal(true);
  };

  const handleViewReport = async (reportId) => {
    try {
      const headerRes = await fetch(`${API_BASE_URL}api/MRHeader/${reportId}`);
      if (!headerRes.ok) {
        Swal.fire({ 
          icon: 'error', 
          title: 'Not Found', 
          text: 'Could not load report details', 
          confirmButtonColor: '#1e40af' 
        });
        return;
      }

      const headerData = await headerRes.json();
      const detailsRes = await fetch(`${API_BASE_URL}api/MRDetails/by-header/${reportId}`);
      let mrDetails = [];
      if (detailsRes.ok) mrDetails = await detailsRes.json();

      setSelectedReport({ 
        ...headerData, 
        mrDetails: Array.isArray(mrDetails) ? mrDetails : [], 
        isGrouped: false 
      });
      setShowViewModal(true);
    } catch (error) {
      Swal.fire({ 
        icon: 'error', 
        title: 'Error', 
        text: 'Failed to load report details', 
        confirmButtonColor: '#1e40af' 
      });
    }
  };

  const handleDeleteReport = async (report) => {
    const result = await Swal.fire({
      title: 'Delete Report?',
      html: `
        <div class="text-left">
          <p class="mb-2"><strong>Control No:</strong> ${report.controlNo || 'N/A'}</p>
          <p class="mb-2"><strong>Name:</strong> ${report.requestedByName}</p>
          <p class="mb-2"><strong>Department:</strong> ${report.department}</p>
          <p class="mt-4 text-red-600 font-semibold">⚠️ This action cannot be undone!</p>
        </div>
      `,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#dc2626',
      cancelButtonColor: '#6b7280',
      confirmButtonText: 'Yes, delete it!'
    });

    if (!result.isConfirmed) return;

    try {
      const res = await fetch(`${API_BASE_URL}api/MR_Header/${report.id}`, {
        method: 'DELETE'
      });

      if (res.ok) {
        await Swal.fire({
          icon: 'success',
          title: 'Deleted!',
          text: 'Report deleted successfully',
          confirmButtonColor: '#10b981',
          timer: 2000
        });
        fetchReports();
      }
    } catch (error) {
      console.error('Error deleting report:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Failed to delete report',
        confirmButtonColor: '#dc2626'
      });
    }
  };

  const handleLogoUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      Swal.fire({
        icon: 'error',
        title: 'Invalid File',
        text: 'Please upload an image file (PNG, JPG, etc.)',
        confirmButtonColor: '#1e40af'
      });
      return;
    }

    if (file.size > 2 * 1024 * 1024) {
      Swal.fire({
        icon: 'error',
        title: 'File Too Large',
        text: 'File size must be less than 2MB',
        confirmButtonColor: '#1e40af'
      });
      return;
    }

    const reader = new FileReader();
    reader.onloadend = () => {
      setCustomLogo(reader.result);
      setShowLogoUploader(false);
      Swal.fire({
        icon: 'success',
        title: 'Logo Uploaded!',
        text: 'Your custom logo has been set',
        timer: 2000,
        confirmButtonColor: '#10b981'
      });
    };
    reader.readAsDataURL(file);
  };

  const handleResetLogo = () => {
    Swal.fire({
      title: 'Reset Logo?',
      text: 'This will restore the default company logo',
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: '#1e40af',
      cancelButtonColor: '#6b7280',
      confirmButtonText: 'Yes, reset it',
      cancelButtonText: 'Cancel'
    }).then((result) => {
      if (result.isConfirmed) {
        setCustomLogo(null);
        setShowLogoUploader(false);
        Swal.fire({
          icon: 'success',
          title: 'Logo Reset!',
          text: 'Default logo restored',
          timer: 2000,
          confirmButtonColor: '#10b981'
        });
      }
    });
  };

  const getLogoSrc = () => {
    if (customLogo) return customLogo;
    return megalogo;
  };

  // ✅ EXPORT PDF WITH SELECTED NAME
  const exportToPDF = (report) => {
    const doc = new jsPDF();
    const logoSrc = getLogoSrc();

    const requestorName = report.isGrouped && selectedNameForPDF 
      ? selectedNameForPDF.name 
      : report.requestedByName;

    doc.setDrawColor(0, 0, 0);
    doc.setLineWidth(0.5);
    let y = 10;

    if (logoSrc) {
      try {
        doc.addImage(logoSrc, 'PNG', 12, y, 25, 12);
      } catch (err) {
        console.error('Error adding logo to PDF:', err);
      }
    }

    doc.setFontSize(6);
    doc.setFont(undefined, 'normal');
    doc.setTextColor(100, 100, 100);
    if (websiteInfo?.websiteSlogan) {
      doc.setFont(undefined, 'italic');
      doc.text(websiteInfo.websiteSlogan, 40, y + 3);
      doc.setFont(undefined, 'normal');
    }
    if (websiteInfo?.websiteName) {
      doc.text(websiteInfo.websiteName, 40, y + 6);
    }
    if (websiteInfo?.websiteContact) {
      doc.text(websiteInfo.websiteContact, 40, y + 9);
    }
    doc.setTextColor(0, 0, 0);

    doc.setFontSize(13);
    doc.setFont(undefined, 'bold');
    doc.text('Spare Parts Item Requests', 105, y + 8, { align: 'center' });

    doc.rect(170, y, 30, 12);
    doc.setFontSize(6);
    doc.setFont(undefined, 'normal');
    doc.text('Document Code:', 172, y + 5);
    doc.setFontSize(5.5);
    doc.text('SPR-F-04-SP-REQ.F-001', 172, y + 9);

    y += 15;



    doc.setLineWidth(0.3);
    doc.rect(10, y, 35, 8);
    doc.setFontSize(7); doc.setFont(undefined, 'bold');
    doc.text('Date:', 12, y + 3);
    doc.setFont(undefined, 'normal'); doc.setFontSize(6.5);
    doc.text(formatDate(report.mrDate), 12, y + 6);

    doc.rect(45, y, 70, 8);
    doc.setFontSize(7); doc.setFont(undefined, 'bold');
    doc.text('Name:', 47, y + 3);
    doc.setFont(undefined, 'normal'); doc.setFontSize(6.5);
    doc.text(requestorName || 'N/A', 47, y + 6);

    doc.rect(115, y, 85, 8);
    doc.setFontSize(7); doc.setFont(undefined, 'bold');
    doc.text('Good Issuance No.:', 117, y + 3);
    doc.setFont(undefined, 'normal'); doc.setFontSize(6.5);
    doc.text(report.arInvoiceNo || '—', 117, y + 6);
    y += 8;

    doc.rect(10, y, 105, 8);
    doc.setFontSize(7); doc.setFont(undefined, 'bold');
    doc.text('Department:', 12, y + 3);
    doc.setFont(undefined, 'normal'); doc.setFontSize(6.5);
    doc.text(report.department || report.departmentName || 'N/A', 12, y + 6);

    doc.rect(115, y, 85, 8);
    doc.setFontSize(7); doc.setFont(undefined, 'bold');
    doc.text('Control No.:', 117, y + 3);
    doc.setFont(undefined, 'normal'); doc.setFontSize(6.5);
    doc.text(report.controlNo || 'N/A', 117, y + 6);
    y += 8;

    doc.rect(10, y, 190, 6);
    doc.setFontSize(6);
    const dept1 = ['Mechanical', 'QC', 'Accounting', 'Logistics', 'Spareparts', 'Gen. Services', 'Production'];
    let xPos = 12;
    dept1.forEach(d => {
      doc.text(`${report.department === d ? '[X]' : '[ ]'} ${d}`, xPos, y + 4);
      xPos += 27;
    });
    y += 6;

    doc.rect(10, y, 190, 6);
    const dept2 = ['RM Whse', 'Packaging', 'Electrical', 'FG Whse', 'Motorpool', 'Admin/HR/MIS', 'Fabrication'];
    xPos = 12;
    dept2.forEach(d => {
      doc.text(`${report.department === d ? '[X]' : '[ ]'} ${d}`, xPos, y + 4);
      xPos += 27;
    });
    y += 8;

    if (report.remarks) {
      doc.setFillColor(254, 243, 199);
      doc.rect(10, y, 190, 8, 'F');
      doc.rect(10, y, 190, 8);
      doc.setFontSize(6); doc.setFont(undefined, 'bold');
      doc.text('REMARKS:', 12, y + 3);
      doc.setFont(undefined, 'normal'); doc.setFontSize(5.5);
      doc.text(doc.splitTextToSize(report.remarks, 175), 12, y + 6);
      y += 10;
    }

    const tableData = (report.mrDetails || []).map((item) => [
      item.itemCode,
      item.requestedQty.toString(),
      item.uom,
      item.itemDescription.length > 45 ? item.itemDescription.substring(0, 45) + '...' : item.itemDescription,
      item.purpose || '—'
    ]);
    while (tableData.length < 6) tableData.push(['', '', '', '', '']);

    autoTable(doc, {
      startY: y,
      head: [['Item Code', 'Qty.', 'UOM', 'Item Description', 'Purpose']],
      body: tableData,
      theme: 'grid',
      headStyles: { fillColor: [240, 240, 240], textColor: 0, fontSize: 7, fontStyle: 'bold', halign: 'left', lineWidth: 0.3, lineColor: 0, cellPadding: 2 },
      bodyStyles: { fontSize: 6.5, lineWidth: 0.3, lineColor: 0, cellPadding: 2 },
      columnStyles: {
        0: { cellWidth: 25, halign: 'left', fontStyle: 'bold' },
        1: { cellWidth: 12, halign: 'center' },
        2: { cellWidth: 12, halign: 'center' },
        3: { cellWidth: 90, halign: 'left' },
        4: { cellWidth: 51, halign: 'left' }
      },
      margin: { left: 10, right: 10 }
    });

    const finalY = doc.lastAutoTable.finalY + 4;

    doc.rect(10, finalY, 95, 25);
    doc.setFontSize(7); doc.setFont(undefined, 'bold');
    doc.text('Requested By:', 12, finalY + 4);
    doc.setFont(undefined, 'normal'); doc.setFontSize(6.5);
    doc.text(requestorName || 'N/A', 12, finalY + 16);
    doc.line(12, finalY + 18, 100, finalY + 18);
    doc.setFontSize(5.5); doc.setTextColor(100, 100, 100);
    doc.text("(Requestor's Signature & Date)", 12, finalY + 22);
    doc.setTextColor(0, 0, 0);

    doc.rect(105, finalY, 95, 25);
    doc.setFontSize(7); doc.setFont(undefined, 'bold');
    doc.text('Approved By:', 107, finalY + 4);
    doc.setFont(undefined, 'normal'); doc.setFontSize(6.5);
    if (report.approvedByName) {
      doc.text(report.approvedByName, 107, finalY + 14);
      if (report.approvedDate) doc.text(formatDate(report.approvedDate), 107, finalY + 18);
    }
    doc.line(107, finalY + 18, 195, finalY + 18);
    doc.setFontSize(5.5); doc.setTextColor(100, 100, 100);
    doc.text("(Supervisor/Immediate Supervisor)", 107, finalY + 21);
    doc.text("Signature & Date", 107, finalY + 23.5);
    doc.setTextColor(0, 0, 0);

    const relY = finalY + 25;
    doc.rect(10, relY, 190, 25);
    doc.setFontSize(7); doc.setFont(undefined, 'bold');
    doc.text('Released By:', 12, relY + 4);
    doc.setFont(undefined, 'normal'); doc.setFontSize(6.5);
    if (report.releasedByName) {
      doc.text(report.releasedByName, 12, relY + 14);
      if (report.releasedDate) doc.text(formatDate(report.releasedDate), 12, relY + 18);
    }
    doc.line(12, relY + 18, 195, relY + 18);
    doc.setFontSize(5.5); doc.setTextColor(100, 100, 100);
    doc.text("(Spare Parts Personnel's) Signature & Date", 12, relY + 22);
    doc.setTextColor(0, 0, 0);

    doc.setFontSize(6); doc.setTextColor(128, 128, 128);
    doc.text(`Generated on ${new Date().toLocaleString()}`, 105, 287, { align: 'center' });
    doc.save(`SPR_${report.controlNo || report.mrNo}_${new Date().toISOString().split('T')[0]}.pdf`);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getStatusBadge = (status) => {
    const badges = {
      '-1': { label: 'Rejected', color: 'bg-red-100 text-red-700 border-red-200', icon: XCircle },
      '0': { label: 'Draft', color: 'bg-gray-100 text-gray-700 border-gray-200', icon: FileText },
      '1': { label: 'Pending', color: 'bg-amber-100 text-amber-700 border-amber-200', icon: Clock },
      '2': { label: 'Approved', color: 'bg-green-100 text-green-700 border-green-200', icon: CheckCircle },
      '3': { label: 'Issued', color: 'bg-blue-100 text-blue-700 border-blue-200', icon: CheckCircle },
      '4': { label: 'Cancelled', color: 'bg-gray-100 text-gray-700 border-gray-200', icon: XCircle }
    };
    const badge = badges[String(status)] || badges['0'];
    const Icon = badge.icon;
    return (
      <span className={`px-3 py-1.5 rounded-lg text-xs font-bold ${badge.color} border inline-flex items-center gap-1.5`}>
        <Icon className="w-3.5 h-3.5" />
        {badge.label}
      </span>
    );
  };

  const getUniqueDepartments = () => {
    const depts = reports.map(r => r.department || r.departmentName).filter(Boolean);
    return [...new Set(depts)].sort();
  };

  const getStatusCount = (status) => {
    return reports.filter(r => r.status === status).length;
  };

  const getDisplayItems = (items) => {
    const actualItems = items || [];
    const emptyRowsNeeded = Math.max(0, 5 - actualItems.length);
    const emptyRows = Array(emptyRowsNeeded).fill({
      itemCode: '',
      requestedQty: '',
      uom: '',
      itemDescription: '',
      purpose: ''
    });
    return [...actualItems, ...emptyRows];
  };

  const totalPages = Math.ceil(filteredReports.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const currentReports = filteredReports.slice(startIndex, startIndex + itemsPerPage);

  const logoSrc = getLogoSrc();

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-blue-50 via-white to-blue-50">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-blue-500 border-t-transparent mb-4"></div>
          <p className="text-gray-600 font-medium">Loading Reports...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-blue-50 p-6">
      <div className="max-w-8xl mx-auto">
        
        {/* Header */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8 mb-6">
          <div className="flex items-center justify-between mb-8">
            <div className="flex items-center gap-4">
              <div className="bg-gradient-to-br from-blue-500 to-blue-600 p-4 rounded-xl shadow-lg shadow-blue-200">
                <FileText className="w-8 h-8 text-white" />
              </div>
              <div>
                <h1 className="text-3xl font-bold text-gray-900">Spare Parts Item Request Reports</h1>
                <p className="text-gray-500 mt-1">Create and manage spare parts request forms</p>
              </div>
            </div>
            <button
              onClick={() => {
                resetForm();
                setShowFormModal(true);
              }}
              className="flex items-center gap-2 bg-blue-600 text-white px-6 py-3 rounded-xl hover:bg-blue-700 transition-all shadow-md hover:shadow-lg font-medium"
            >
              <Plus className="w-5 h-5" />
              Create Report
            </button>
          </div>

          {/* Stats Cards */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
            <div className="bg-gradient-to-br from-gray-50 to-gray-100 border border-gray-200 rounded-xl p-5 hover:shadow-md transition-all">
              <FileText className="w-10 h-10 text-gray-500 mb-2" />
              <p className="text-gray-600 text-xs font-semibold uppercase tracking-wide mb-1">Draft</p>
              <p className="text-3xl font-bold text-gray-900">{getStatusCount(0)}</p>
            </div>
            <div className="bg-gradient-to-br from-amber-50 to-amber-100 border border-amber-200 rounded-xl p-5 hover:shadow-md transition-all">
              <Clock className="w-10 h-10 text-amber-500 mb-2" />
              <p className="text-amber-700 text-xs font-semibold uppercase tracking-wide mb-1">Pending</p>
              <p className="text-3xl font-bold text-amber-900">{getStatusCount(1)}</p>
            </div>
            <div className="bg-gradient-to-br from-green-50 to-green-100 border border-green-200 rounded-xl p-5 hover:shadow-md transition-all">
              <CheckCircle className="w-10 h-10 text-green-500 mb-2" />
              <p className="text-green-700 text-xs font-semibold uppercase tracking-wide mb-1">Approved</p>
              <p className="text-3xl font-bold text-green-900">{getStatusCount(2)}</p>
            </div>
            <div className="bg-gradient-to-br from-blue-50 to-blue-100 border border-blue-200 rounded-xl p-5 hover:shadow-md transition-all">
              <CheckCircle className="w-10 h-10 text-blue-500 mb-2" />
              <p className="text-blue-700 text-xs font-semibold uppercase tracking-wide mb-1">Issued</p>
              <p className="text-3xl font-bold text-blue-900">{getStatusCount(3)}</p>
            </div>
            <div className="bg-gradient-to-br from-red-50 to-red-100 border border-red-200 rounded-xl p-5 hover:shadow-md transition-all">
              <XCircle className="w-10 h-10 text-red-500 mb-2" />
              <p className="text-red-700 text-xs font-semibold uppercase tracking-wide mb-1">Rejected</p>
              <p className="text-3xl font-bold text-red-900">{getStatusCount(-1)}</p>
            </div>
          </div>

          {/* Filters */}
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                placeholder="Search by Control No, Good Issuance No, Name, Department..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-12 pr-4 py-3 border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:outline-none transition-all"
              />
            </div>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:outline-none bg-white min-w-[180px] transition-all"
            >
              <option value="all">All Status</option>
              <option value="0">Draft</option>
              <option value="1">Pending</option>
              <option value="2">Approved</option>
              <option value="3">Issued</option>
              <option value="-1">Rejected</option>
            </select>
            <select
              value={departmentFilter}
              onChange={(e) => setDepartmentFilter(e.target.value)}
              className="px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:outline-none bg-white min-w-[200px] transition-all"
            >
              <option value="all">All Departments</option>
              {getUniqueDepartments().map(dept => (
                <option key={dept} value={dept}>{dept}</option>
              ))}
            </select>
          </div>
        </div>

        {/* ✅ GROUPED REPORTS SECTION */}
        {groupedReports.length > 0 && (
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 mb-6">
            <div className="flex items-center gap-3 mb-4">
              <Users className="w-6 h-6 text-purple-600" />
              <h2 className="text-xl font-bold text-gray-900">Grouped Reports</h2>
              <span className="px-3 py-1 bg-purple-100 text-purple-700 rounded-full text-sm font-bold">
                {groupedReports.filter(g => g.names.length > 1).length} Groups
              </span>
            </div>
            
            <div className="space-y-3">
              {groupedReports
                .filter(group => group.names.length > 1)
                .map((group, idx) => (
                  <div key={idx} className="border-2 border-purple-200 rounded-xl p-4 hover:shadow-md transition-all bg-purple-50">
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <Building2 className="w-5 h-5 text-purple-600" />
                          <span className="font-bold text-gray-900">{group.department}</span>
                          <span className="text-gray-400">•</span>
                          <FileText className="w-4 h-4 text-purple-600" />
                          <span className="font-mono font-bold text-purple-900">{group.controlNo}</span>
                        </div>
                        <div className="flex items-center gap-4 text-sm text-gray-600">
                          <div className="flex items-center gap-2">
                            <Users className="w-4 h-4" />
                            <span className="font-semibold">{group.names.length} Requestors:</span>
                            <span>{group.names.map(n => n.name).join(', ')}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <Package className="w-4 h-4" />
                            <span className="font-bold text-purple-700">{group.allItems.length} Total Items</span>
                          </div>
                        </div>
                      </div>
                      <button
                        onClick={() => handleViewGroupedReport(group)}
                        className="inline-flex items-center gap-2 px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-xl transition-all shadow-md hover:shadow-lg text-sm font-semibold"
                      >
                        <Eye className="w-4 h-4" />
                        View Grouped
                      </button>
                    </div>
                  </div>
                ))}
            </div>
          </div>
        )}

        {/* Reports Table */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
          {filteredReports.length === 0 ? (
            <div className="text-center py-20">
              <Package className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500 text-lg font-medium">No reports found</p>
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gradient-to-r from-blue-600 to-blue-700 text-white">
                    <tr>
                      <th className="px-6 py-4 text-left text-xs font-bold uppercase tracking-wider">Date</th>
                      <th className="px-6 py-4 text-left text-xs font-bold uppercase tracking-wider">Name</th>
                      <th className="px-6 py-4 text-left text-xs font-bold uppercase tracking-wider">Good Issuance No.</th>
                      <th className="px-6 py-4 text-left text-xs font-bold uppercase tracking-wider">Department</th>
                      <th className="px-6 py-4 text-left text-xs font-bold uppercase tracking-wider">Control No.</th>
                      <th className="px-6 py-4 text-center text-xs font-bold uppercase tracking-wider">Items</th>
                      <th className="px-6 py-4 text-center text-xs font-bold uppercase tracking-wider">Status</th>
                      <th className="px-6 py-4 text-center text-xs font-bold uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {currentReports.map((report) => (
                      <tr key={report.id} className="hover:bg-blue-50 transition-colors">
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-2">
                            <Calendar className="w-4 h-4 text-blue-600" />
                            <span className="text-sm font-medium text-gray-900">{formatDate(report.mrDate)}</span>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-2">
                            <User className="w-4 h-4 text-blue-600" />
                            <span className="text-sm font-medium text-gray-900">{report.requestedByName}</span>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <span className="text-sm font-mono font-bold text-blue-900">{report.arInvoiceNo || '—'}</span>
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-2">
                            <Building2 className="w-4 h-4 text-blue-600" />
                            <span className="text-sm font-medium text-gray-900">{report.department || report.departmentName || 'N/A'}</span>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <span className="inline-flex items-center gap-1.5 bg-blue-100 text-blue-800 px-3 py-1.5 rounded-lg text-xs font-bold border border-blue-200">
                            <FileText className="w-3.5 h-3.5" />
                            {report.controlNo || report.mrNo}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-center">
                          <span className="inline-flex items-center gap-1.5 bg-indigo-100 text-indigo-700 px-3 py-1.5 rounded-lg text-xs font-bold border border-indigo-200">
                            <Package className="w-3.5 h-3.5" />
                            {report.mrDetails?.length || 0}
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex justify-center">
                            {getStatusBadge(report.status)}
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex items-center justify-center gap-2">
                            <button
                              onClick={() => handleViewReport(report.id)}
                              className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-xl transition-all shadow-md hover:shadow-lg text-sm font-semibold"
                            >
                              <Eye className="w-4 h-4" />
                              View
                            </button>
                            <button
                              onClick={() => handleDeleteReport(report)}
                              className="inline-flex items-center gap-2 px-3 py-2 bg-red-600 hover:bg-red-700 text-white rounded-xl transition-all shadow-md hover:shadow-lg text-sm font-semibold"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {totalPages > 1 && (
                <div className="bg-gray-50 px-6 py-4 border-t border-gray-100 flex items-center justify-between">
                  <p className="text-sm text-gray-600">
                    Showing <span className="font-bold text-gray-900">{startIndex + 1}</span> to{' '}
                    <span className="font-bold text-gray-900">{Math.min(startIndex + itemsPerPage, filteredReports.length)}</span> of{' '}
                    <span className="font-bold text-gray-900">{filteredReports.length}</span> reports
                  </p>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                      disabled={currentPage === 1}
                      className="px-4 py-2 rounded-lg border border-gray-200 text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100 transition-all flex items-center gap-1"
                    >
                      <ChevronLeft className="w-4 h-4" /> Previous
                    </button>
                    <div className="flex items-center gap-1">
                      {[...Array(totalPages)].map((_, idx) => {
                        const pageNum = idx + 1;
                        if (pageNum === 1 || pageNum === totalPages || (pageNum >= currentPage - 1 && pageNum <= currentPage + 1)) {
                          return (
                            <button key={pageNum} onClick={() => setCurrentPage(pageNum)}
                              className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${currentPage === pageNum ? 'bg-blue-600 text-white shadow-md' : 'border border-gray-200 hover:bg-gray-100'}`}>
                              {pageNum}
                            </button>
                          );
                        } else if (pageNum === currentPage - 2 || pageNum === currentPage + 2) {
                          return <span key={pageNum} className="px-2 text-gray-400">...</span>;
                        }
                        return null;
                      })}
                    </div>
                    <button
                      onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                      disabled={currentPage === totalPages}
                      className="px-4 py-2 rounded-lg border border-gray-200 text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100 transition-all flex items-center gap-1"
                    >
                      Next <ChevronRight className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* CREATE/EDIT FORM MODAL - (keeping original form modal code here - truncated for space) */}
      {showFormModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          {/* ... (form modal content same as original) ... */}
        </div>
      )}

      {/* ✅ VIEW MODAL WITH NAME SELECTOR */}
      {showViewModal && selectedReport && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-6xl max-h-[90vh] overflow-hidden flex flex-col">
            
            {/* Modal Header */}
            <div className="bg-white border-b-2 border-gray-400 px-8 py-6 rounded-t-2xl flex-shrink-0">
              <div className="flex items-start justify-between mb-4">
                
                {/* Left: Logo */}
                <div className="flex items-center gap-3">
                  <div className="relative inline-block group">
                    {logoSrc ? (
                      <div className="flex flex-col items-center">
                        <img
                          src={logoSrc}
                          alt="Company Logo"
                          className="h-16 w-auto object-contain"
                          onError={(e) => {
                            e.target.style.display = 'none';
                          }}
                        />

                        <div className="no-print absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity rounded-lg flex items-center justify-center gap-2">
                          <button
                            onClick={() => setShowLogoUploader(true)}
                            className="p-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg transition-all"
                            title="Change Logo"
                          >
                            <Upload size={16} />
                          </button>
                          {customLogo && (
                            <button
                              onClick={handleResetLogo}
                              className="p-2 bg-gray-500 hover:bg-gray-600 text-white rounded-lg transition-all"
                              title="Reset to Default"
                            >
                              <RotateCcw size={16} />
                            </button>
                          )}
                        </div>
                      </div>
                    ) : (
                      <div
                        onClick={() => setShowLogoUploader(true)}
                        className="no-print h-20 w-32 border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center hover:border-blue-500 hover:bg-blue-50 transition-all cursor-pointer"
                      >
                        <div className="text-center">
                          <Upload size={24} className="mx-auto text-gray-400 mb-1" />
                          <p className="text-xs text-gray-500">Upload Logo</p>
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="text-sm text-gray-700 leading-relaxed">
                    {websiteInfo?.websiteSlogan && (<p className="italic font-medium mb-1">{websiteInfo.websiteSlogan}</p>)}
                    {websiteInfo?.websiteName && (<p className="font-semibold mb-1">{websiteInfo.websiteName}</p>)}
                    {websiteInfo?.websiteContact && (<p className="font-medium">{websiteInfo.websiteContact}</p>)}
                  </div>
                </div>

                {/* Center: Title */}
                <div className="text-center flex-1">
                  <h2 className="text-2xl font-bold text-gray-800">Spare Parts Item Requests</h2>
                  <p className="text-sm text-gray-600 mt-1">Document Code: <span className="font-semibold">SPR-F-04-SP-REQ.F-001</span></p>
                  
                  {/* ✅ NAME SELECTOR FOR GROUPED REPORTS */}
                  {selectedReport.isGrouped && selectedReport.groupedNames && (
                    <div className="mt-3 flex items-center justify-center gap-2">
                      <Users className="w-4 h-4 text-purple-600" />
                      <span className="text-xs font-medium text-gray-600">Select Requestor:</span>
                      <select
                        value={selectedNameForPDF?.id || ''}
                        onChange={(e) => {
                          const selected = selectedReport.groupedNames.find(n => n.id === parseInt(e.target.value));
                          setSelectedNameForPDF(selected);
                        }}
                        className="px-3 py-1.5 border-2 border-purple-300 rounded-lg text-sm font-semibold text-purple-900 focus:border-purple-500 focus:outline-none bg-purple-50"
                      >
                        {selectedReport.groupedNames.map(nameObj => (
                          <option key={nameObj.id} value={nameObj.id}>
                            {nameObj.name}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}
                </div>

                {/* Right: Actions */}
                <div className="flex items-start gap-3">
                  <div className="text-right">
                    <span className="text-sm font-medium text-gray-500">Control Number:</span>
                    <div className="px-3 py-1 bg-blue-100 text-blue-800 rounded-lg font-mono text-sm font-semibold mt-1">
                      {selectedReport.controlNo || selectedReport.mrNo}
                    </div>
                  </div>

                  <div className="no-print flex gap-2">
                    <button
                      onClick={() => exportToPDF(selectedReport)}
                      className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                      title="Download PDF"
                    >
                      <Download className="h-5 w-5 text-gray-700" />
                    </button>
                    <button
                      onClick={() => setShowViewModal(false)}
                      className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                    >
                      <X className="h-6 w-6" />
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Modal Body */}
            <div className="p-8 bg-white flex-1 overflow-y-auto">
              <div className="border-2 border-gray-300 mb-4">
                <div className="grid grid-cols-12 border-b-2 border-gray-300">
                  <div className="col-span-2 border-r-2 border-gray-300 p-3">
                    <p className="text-xs font-bold text-gray-900 mb-1">Date:</p>
                    <p className="text-sm text-gray-700">{formatDate(selectedReport.mrDate)}</p>
                  </div>
                  <div className="col-span-5 border-r-2 border-gray-300 p-3">
                    <p className="text-xs font-bold text-gray-900 mb-1">Name:</p>
                    <p className="text-sm text-gray-700">
                      {selectedReport.isGrouped && selectedNameForPDF 
                        ? selectedNameForPDF.name 
                        : selectedReport.requestedByName}
                    </p>
                  </div>
                  <div className="col-span-5 p-3">
                    <p className="text-xs font-bold text-gray-900 mb-1">Good Issuance No.:</p>
                    <p className="text-sm text-gray-700">{selectedReport.arInvoiceNo || '—'}</p>
                  </div>
                </div>
                <div className="grid grid-cols-12 border-b-2 border-gray-300">
                  <div className="col-span-7 border-r-2 border-gray-300 p-3">
                    <p className="text-xs font-bold text-gray-900 mb-1">Department:</p>
                    <p className="text-sm text-gray-700">{selectedReport.department || selectedReport.departmentName || 'N/A'}</p>
                  </div>
                  <div className="col-span-5 p-3">
                    <p className="text-xs font-bold text-gray-900 mb-1">Control No.:</p>
                    <p className="text-sm text-gray-700">{selectedReport.controlNo || selectedReport.mrNo}</p>
                  </div>
                </div>
                <div className="p-3 border-b-2 border-gray-300">
                  <div className="grid grid-cols-7 gap-2 text-xs">
                    {['Mechanical', 'QC', 'Accounting', 'Logistics', 'Spareparts', 'Gen. Services', 'Production'].map(d => (
                      <label key={d} className="flex items-center gap-1">
                        <input type="checkbox" className="w-3 h-3" disabled checked={selectedReport.department === d} />
                        <span>{d}</span>
                      </label>
                    ))}
                  </div>
                </div>
                <div className="p-3">
                  <div className="grid grid-cols-7 gap-2 text-xs">
                    {['RM Whse', 'Packaging', 'Electrical', 'FG Whse', 'Motorpool', 'Admin/HR/MIS', 'Fabrication'].map(d => (
                      <label key={d} className="flex items-center gap-1">
                        <input type="checkbox" className="w-3 h-3" disabled checked={selectedReport.department === d} />
                        <span>{d}</span>
                      </label>
                    ))}
                  </div>
                  {selectedReport.branchSpecify && (
                    <div className="mt-2">
                      <span className="text-xs font-bold">Branch Specify: </span>
                      <span className="text-xs">{selectedReport.branchSpecify}</span>
                    </div>
                  )}
                </div>
              </div>

              {selectedReport.remarks && (
                <div className="bg-amber-50 border-l-4 border-amber-500 p-4 mb-4">
                  <p className="text-xs text-amber-700 uppercase font-bold mb-1">REMARKS</p>
                  <p className="text-sm text-gray-800">{selectedReport.remarks}</p>
                </div>
              )}

              <div className="border-2 border-gray-300 overflow-hidden mb-4">
                <table className="w-full">
                  <thead className="bg-gray-100 border-b-2 border-gray-300">
                    <tr>
                      <th className="px-4 py-2 text-left text-xs font-bold text-gray-900 uppercase border-r-2 border-gray-300">Item Code</th>
                      <th className="px-4 py-2 text-center text-xs font-bold text-gray-900 uppercase border-r-2 border-gray-300">Qty.</th>
                      <th className="px-4 py-2 text-center text-xs font-bold text-gray-900 uppercase border-r-2 border-gray-300">UOM</th>
                      <th className="px-4 py-2 text-left text-xs font-bold text-gray-900 uppercase border-r-2 border-gray-300">Item Description</th>
                      <th className="px-4 py-2 text-left text-xs font-bold text-gray-900 uppercase">Purpose</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(selectedReport.mrDetails || []).map((item, index) => (
                      <tr key={item.id || index} className="border-b border-gray-300">
                        <td className="px-4 py-3 border-r-2 border-gray-300 text-sm font-bold text-gray-900">{item.itemCode}</td>
                        <td className="px-4 py-3 text-center border-r-2 border-gray-300 text-sm text-gray-900">{item.requestedQty}</td>
                        <td className="px-4 py-3 text-center border-r-2 border-gray-300 text-sm text-gray-900">{item.uom}</td>
                        <td className="px-4 py-3 border-r-2 border-gray-300 text-sm text-gray-900">{item.itemDescription}</td>
                        <td className="px-4 py-3 text-sm text-gray-900">{item.purpose || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="bg-white border-t-2 border-gray-300 p-8 rounded-b-2xl flex-shrink-0">
              <div className="border-2 border-gray-300">
                <div className="grid grid-cols-2 border-b-2 border-gray-300">
                  <div className="border-r-2 border-gray-300 p-4">
                    <p className="text-xs font-bold text-gray-900 mb-2">Requested By:</p>
                    <div className="h-16 flex items-end border-b-2 border-gray-800 mb-2">
                      <p className="text-sm font-bold text-gray-900 pb-1">
                        {selectedReport.isGrouped && selectedNameForPDF 
                          ? selectedNameForPDF.name 
                          : selectedReport.requestedByName}
                      </p>
                    </div>
                    <p className="text-xs text-gray-600 italic text-center">(Requestor's Signature & Date)</p>
                  </div>
                  <div className="p-4">
                    <p className="text-xs font-bold text-gray-900 mb-2">Approved By:</p>
                    <div className="h-16 flex flex-col items-start justify-end border-b-2 border-gray-800 mb-2">
                      {selectedReport.approvedByName && (
                        <>
                          <p className="text-sm font-bold text-gray-900">{selectedReport.approvedByName}</p>
                          {selectedReport.approvedDate && (
                            <p className="text-xs text-gray-600">{formatDate(selectedReport.approvedDate)}</p>
                          )}
                        </>
                      )}
                    </div>
                    <p className="text-xs text-gray-600 italic text-center">(Supervisor/Immediate Supervisor) Signature & Date</p>
                  </div>
                </div>
                <div className="p-4">
                  <p className="text-xs font-bold text-gray-900 mb-2">Released By:</p>
                  <div className="h-16 flex flex-col items-start justify-end border-b-2 border-gray-800 mb-2">
                    {selectedReport.releasedByName && (
                      <>
                        <p className="text-sm font-bold text-gray-900">{selectedReport.releasedByName}</p>
                        {selectedReport.releasedDate && (
                          <p className="text-xs text-gray-600">{formatDate(selectedReport.releasedDate)}</p>
                        )}
                      </>
                    )}
                  </div>
                  <p className="text-xs text-gray-600 italic text-center">(Spare Parts Personnel's) Signature & Date</p>
                </div>
              </div>
              <div className="flex justify-end mt-6">
                <button onClick={() => setShowViewModal(false)} className="px-6 py-2.5 bg-gray-600 hover:bg-gray-700 text-white rounded-lg font-semibold transition-all shadow-md">
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Logo Uploader Modal */}
      {showLogoUploader && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[60] p-4">
          <div className="bg-white rounded-2xl shadow-2xl p-6 w-full max-w-md">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-bold text-gray-800">Upload Custom Logo</h3>
              <button
                onClick={() => setShowLogoUploader(false)}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X size={20} />
              </button>
            </div>

            <div className="space-y-4">
              <div
                className="border-2 border-dashed border-gray-300 rounded-xl p-8 text-center hover:border-blue-500 hover:bg-blue-50 transition-all cursor-pointer"
                onClick={() => document.getElementById('logoInput').click()}
              >
                <Upload size={48} className="mx-auto text-gray-400 mb-3" />
                <p className="text-gray-700 font-medium mb-1">Click to upload logo</p>
                <p className="text-sm text-gray-500">PNG, JPG up to 2MB</p>
              </div>

              <input
                type="file"
                id="logoInput"
                accept="image/*"
                onChange={handleLogoUpload}
                className="hidden"
              />

              {customLogo && (
                <div className="flex gap-2">
                  <button
                    onClick={handleResetLogo}
                    className="flex-1 py-2 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-lg font-medium transition-colors"
                  >
                    Reset to Default
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <style jsx>{`
        @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
        @keyframes slideUp { from { opacity: 0; transform: translateY(20px); } to { opacity: 1; transform: translateY(0); } }
        .animate-fadeIn { animation: fadeIn 0.2s ease-out; }
        .animate-slideUp { animation: slideUp 0.3s ease-out; }
        
        @media print {
          .no-print {
            display: none !important;
          }
          
          body {
            print-color-adjust: exact;
            -webkit-print-color-adjust: exact;
          }
          
          @page {
            margin: 1cm;
            size: A4;
          }
        }
      `}</style>
    </div>
  );
}